import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, KeyRound, RefreshCw, Scale } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import {
  INTEGRATIONS_PERMISSIONS,
  INTEGRATIONS_ROUTES,
  invalidateIntegrationDeliveries,
  invalidateIntegrationInstallation,
  invalidateIntegrationJobs,
  useIntegrationCursorsQuery,
  useIntegrationDeliveriesQuery,
  useIntegrationInstallationHealthQuery,
  useIntegrationInstallationQuery,
  useIntegrationJobsQuery,
  type ConfigureInstallationBody,
  type IntegrationInstallationDetail,
  type PutCredentialBody,
  type StartSyncJobBody,
} from '../integrationsApi';
import {
  apiMessage,
  formatDateTime,
  INTEGRATION_REPLAYABLE_STATUSES,
  toneForInstallationHealth,
  toneForInstallationStatus,
  toneForIntegrationDelivery,
  toneForIntegrationJob,
} from '../lib';

const CREDENTIAL_KINDS = [
  'API_KEY',
  'API_SECRET',
  'OAUTH_ACCESS_TOKEN',
  'OAUTH_REFRESH_TOKEN',
  'WEBHOOK_SIGNING_SECRET',
] as const;

const JOB_KINDS = ['BACKFILL', 'SYNC', 'RECONCILE'] as const;

/**
 * One installation, opened from its row — health, credentials, sync and its
 * own dead-letter queue, all facets of one connection, the same shape
 * `MenuGovernance/VersionDetail.tsx` opens a version in.
 */
export default function InstallationDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { confirm } = useConfirm();
  const { can } = usePermission();

  const { data, isFetching, isError, error, refetch } = useIntegrationInstallationQuery(id);
  const reload = () => {
    refetch();
    dispatch(invalidateIntegrationInstallation(id));
  };

  const uninstall = async () => {
    if (!data) return;
    const ok = await confirm({
      title: t('integrations.detail.uninstallTitle'),
      body: t('integrations.detail.uninstallBody'),
      confirmLabel: t('integrations.detail.uninstall'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(INTEGRATIONS_ROUTES.installation(id));
      toast.success(t('integrations.detail.uninstalled'));
      dispatch(invalidateIntegrationInstallation(id));
      onClose();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={data?.provider?.name ?? t('integrations.detail.title')}
      description={data?.providerKey}
      className="max-w-3xl"
      footer={
        <div className="flex justify-between gap-2">
          {data && can(INTEGRATIONS_PERMISSIONS.uninstall) && data.status !== 'UNINSTALLED' && (
            <Button variant="danger" onClick={() => void uninstall()}>
              {t('integrations.detail.uninstall')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} className="ms-auto">
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {isFetching && !data && <LoadingState />}
      {isError && !data && (
        <ErrorState message={apiMessage(error, t('integrations.states.loadFailed'))} onRetry={reload} />
      )}
      {data && (
        <div className="max-h-[70vh] space-y-6 overflow-y-auto pe-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={toneForInstallationStatus(data.status)}>
              {t(`integrations.installationStatus.${data.status}`)}
            </Badge>
            <Badge tone={toneForInstallationHealth(data.health)}>
              {t(`integrations.health.${data.health}`)}
            </Badge>
            <span className="text-sm text-ink-muted">
              {data.branch?.name ?? t('integrations.providers.wholeVenue')}
            </span>
            {/* The gap this closes: installing an ACCOUNTING connector used to
                be the end of the road — nothing here pointed at the screen
                that shows what it actually posted. Gated on `accounting.view`
                rather than this tab's own `integrations.view`, because the
                audience who wants this link (a bookkeeper) is not necessarily
                the one who can open this installation dialog at all (an
                integration engineer) — see `Pages/nav.ts`'s own reasoning for
                why the two screens sit on different permission keys. */}
            {data.provider.category === 'ACCOUNTING' && can('accounting.view') && (
              <Link
                to="/accounting"
                className="inline-flex items-center gap-1 text-sm font-medium text-accent underline"
              >
                <Icon as={Scale} size="sm" />
                {t('integrations.detail.viewPostings')}
              </Link>
            )}
          </div>

          {data.missingCredentials.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
              {t('integrations.detail.missingCredentials', {
                kinds: data.missingCredentials.join(', '),
              })}
            </div>
          )}

          <ConfigureSection installationId={id} installation={data} onSaved={reload} />
          <CredentialsSection installationId={id} installation={data} onSaved={reload} />
          <HealthSection installationId={id} />
          <SyncSection installationId={id} isActive={data.status === 'ACTIVE'} />
          <DeliveriesSection installationId={id} />
        </div>
      )}
    </Dialog>
  );
}

// ------------------------------------------------------------- configure

function ConfigureSection({
  installationId,
  installation,
  onSaved,
}: {
  installationId: string;
  installation: IntegrationInstallationDetail;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canConfigure = can(INTEGRATIONS_PERMISSIONS.configure);
  const [editing, setEditing] = useState(false);
  const [scopes, setScopes] = useState(installation.grantedScopes.join(', '));
  const [events, setEvents] = useState(installation.subscribedEvents.join(', '));
  const [endpointUrl, setEndpointUrl] = useState(installation.endpointUrl ?? '');
  const [status, setStatus] = useState(installation.status === 'PENDING' ? '' : installation.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gates the button, not only the route: `POST .../oauth/authorize` requires
  // `integrations.configure` on the backend, and a button that skipped this
  // check would 403 an actor who can see the installation but not configure it.
  const canConnectOAuth =
    canConfigure &&
    installation.provider.authKind === 'OAUTH2' &&
    installation.status !== 'UNINSTALLED';

  const beginOAuth = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<{ authorizeUrl: string }>(
        INTEGRATIONS_ROUTES.installationOAuthAuthorize(installationId),
      );
      window.open(data.authorizeUrl, '_blank', 'noopener,noreferrer');
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: ConfigureInstallationBody = {
        scopes: scopes.split(',').map((s) => s.trim()).filter(Boolean),
        subscribedEvents: events.split(',').map((s) => s.trim()).filter(Boolean),
        endpointUrl: endpointUrl.trim() || undefined,
        ...(status ? { status: status as 'ACTIVE' | 'SUSPENDED' } : {}),
      };
      await api.patch(INTEGRATIONS_ROUTES.installation(installationId), body);
      toast.success(t('integrations.detail.configureSaved'));
      setEditing(false);
      onSaved();
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3 border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('integrations.detail.sectionConfigure')}</h3>
        <div className="flex gap-2">
          {canConnectOAuth && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void beginOAuth()}>
              <Icon as={ExternalLink} />
              {t('integrations.detail.connectOAuth')}
            </Button>
          )}
          {canConfigure && !editing && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              {t('common.edit')}
            </Button>
          )}
        </div>
      </div>

      {!editing ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-ink-muted">{t('integrations.providers.scopes')}</dt>
            <dd>{installation.grantedScopes.join(', ') || '—'}</dd>
          </div>
          <div>
            <dt className="text-ink-muted">{t('integrations.providers.subscribedEvents')}</dt>
            <dd>{installation.subscribedEvents.join(', ') || '—'}</dd>
          </div>
          <div>
            <dt className="text-ink-muted">{t('integrations.providers.endpointUrl')}</dt>
            <dd className="break-all">{installation.endpointUrl ?? '—'}</dd>
          </div>
        </dl>
      ) : (
        <div className="space-y-3">
          <Field label={t('integrations.providers.scopes')} hint={t('integrations.detail.commaSeparated')}>
            <Input value={scopes} onChange={(e) => setScopes(e.target.value)} />
          </Field>
          <Field
            label={t('integrations.providers.subscribedEvents')}
            hint={t('integrations.detail.commaSeparated')}
          >
            <Input value={events} onChange={(e) => setEvents(e.target.value)} />
          </Field>
          <Field label={t('integrations.providers.endpointUrl')}>
            <Input value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} />
          </Field>
          {installation.status !== 'PENDING' && (
            <Field
              label={t('integrations.installations.status')}
              hint={t('integrations.detail.activateNeedsCredentials')}
            >
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="ACTIVE">{t('integrations.installationStatus.ACTIVE')}</option>
                <option value="SUSPENDED">{t('integrations.installationStatus.SUSPENDED')}</option>
              </Select>
            </Field>
          )}
          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------ credentials

function CredentialsSection({
  installationId,
  installation,
  onSaved,
}: {
  installationId: string;
  installation: IntegrationInstallationDetail;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canRotate = can(INTEGRATIONS_PERMISSIONS.credentialsRotate);
  const [adding, setAdding] = useState(false);

  return (
    <section className="space-y-3 border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('integrations.detail.sectionCredentials')}</h3>
        {canRotate && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Icon as={KeyRound} />
            {t('integrations.detail.storeCredential')}
          </Button>
        )}
      </div>

      {installation.credentials.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('integrations.detail.noCredentials')}</p>
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.detail.credentialKind')}</TH>
                <TH>{t('integrations.detail.publicRef')}</TH>
                <TH>{t('integrations.detail.version')}</TH>
                <TH>{t('integrations.detail.rotatedAt')}</TH>
                <TH>{t('integrations.detail.expiresAt')}</TH>
              </TR>
            </THead>
            <tbody>
              {installation.credentials.map((cred) => (
                <TR key={cred.id}>
                  <TD className="font-medium">{cred.kind}</TD>
                  <TD className="text-sm text-ink-muted">{cred.publicRef ?? '—'}</TD>
                  <TD className="text-sm tabular-nums text-ink-muted">{cred.version}</TD>
                  <TD className="text-sm tabular-nums text-ink-muted">
                    {formatDateTime(cred.rotatedAt, i18n.language)}
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">
                    {formatDateTime(cred.expiresAt, i18n.language)}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {adding && (
        <PutCredentialDialog
          installationId={installationId}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            onSaved();
          }}
        />
      )}
    </section>
  );
}

function PutCredentialDialog({
  installationId,
  onClose,
  onSaved,
}: {
  installationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<(typeof CREDENTIAL_KINDS)[number]>('API_KEY');
  const [secret, setSecret] = useState('');
  const [publicRef, setPublicRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (secret.trim().length < 8) {
      setError(t('integrations.detail.secretTooShort'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: PutCredentialBody = {
        kind,
        secret: secret.trim(),
        ...(publicRef.trim() ? { publicRef: publicRef.trim() } : {}),
      };
      await api.put(INTEGRATIONS_ROUTES.installationCredentials(installationId), body);
      toast.success(t('integrations.detail.credentialSaved'));
      onSaved();
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('integrations.detail.storeCredential')}
      className="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/*
            No overlap window here, and the copy says so on purpose — unlike a
            webhook signing secret this platform mints, a provider credential
            is issued and checked by the PROVIDER, so there is no "old and new
            both verify" window this app controls. Replacing it switches every
            future call to the provider over immediately.
        */}
        <p className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
          {t('integrations.detail.credentialNoOverlap')}
        </p>
        <Field label={t('integrations.detail.credentialKind')}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            {CREDENTIAL_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('integrations.detail.secret')} required hint={t('integrations.detail.secretHint')}>
          <Input
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        </Field>
        <Field label={t('integrations.detail.publicRef')} hint={t('integrations.detail.publicRefHint')}>
          <Input value={publicRef} onChange={(e) => setPublicRef(e.target.value)} />
        </Field>
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ----------------------------------------------------------------- health

function HealthSection({ installationId }: { installationId: string }) {
  const { t, i18n } = useTranslation();
  const { data, isFetching, isError, error, refetch } = useIntegrationInstallationHealthQuery(
    installationId,
  );

  return (
    <section className="space-y-3 border-t pt-4">
      <h3 className="text-sm font-semibold">{t('integrations.detail.sectionHealth')}</h3>
      {isFetching && !data && <LoadingState />}
      {isError && !data && (
        <ErrorState message={apiMessage(error, t('integrations.states.loadFailed'))} onRetry={refetch} />
      )}
      {data && (
        <div className="space-y-3">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-ink-muted">{t('integrations.installations.lastSuccess')}</dt>
              <dd className="tabular-nums">{formatDateTime(data.lastSuccessAt, i18n.language)}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">{t('integrations.installations.lastFailure')}</dt>
              <dd className="tabular-nums">{formatDateTime(data.lastFailureAt, i18n.language)}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">{t('integrations.detail.consecutiveFailures')}</dt>
              <dd className="tabular-nums">{data.consecutiveFailures}</dd>
            </div>
          </dl>
          {data.lastError && (
            <p className="rounded-lg border border-red-300 bg-red-50/60 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              {data.lastError}
            </p>
          )}
          <p className="text-xs text-ink-muted">
            {t('integrations.detail.healthWindow', { hours: data.windowHours })}
          </p>
          {data.recentFailures.length > 0 && (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('integrations.deliveries.event')}</TH>
                    <TH>{t('integrations.deliveries.status')}</TH>
                    <TH>{t('integrations.deliveries.attempts')}</TH>
                    <TH>{t('integrations.deliveries.lastError')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {data.recentFailures.map((f) => (
                    <TR key={f.id}>
                      <TD className="text-sm">{f.eventName}</TD>
                      <TD>
                        <Badge tone={toneForIntegrationDelivery(f.status)}>
                          {t(`integrations.deliveryStatus.${f.status}`)}
                        </Badge>
                      </TD>
                      <TD className="text-sm tabular-nums">{f.attempts}</TD>
                      <TD className="max-w-xs truncate text-sm text-ink-muted" title={f.lastError ?? ''}>
                        {f.lastError ?? '—'}
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------- sync

function SyncSection({ installationId, isActive }: { installationId: string; isActive: boolean }) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { can } = usePermission();
  const canSync = can(INTEGRATIONS_PERMISSIONS.sync);

  const cursors = useIntegrationCursorsQuery(installationId);
  const jobs = useIntegrationJobsQuery({ installationId, limit: 10 });

  const [kind, setKind] = useState<(typeof JOB_KINDS)[number]>('SYNC');
  const [resource, setResource] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = () => dispatch(invalidateIntegrationJobs(installationId));

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: StartSyncJobBody = { kind, ...(resource.trim() ? { resource: resource.trim() } : {}) };
      await api.post(INTEGRATIONS_ROUTES.installationJobs(installationId), body);
      toast.success(t('integrations.detail.jobStarted'));
      setResource('');
      reload();
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  const cancelJob = async (jobId: string) => {
    try {
      await api.delete(INTEGRATIONS_ROUTES.job(jobId));
      toast.success(t('integrations.detail.jobCancelled'));
      reload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  return (
    <section className="space-y-3 border-t pt-4">
      <h3 className="text-sm font-semibold">{t('integrations.detail.sectionSync')}</h3>

      {cursors.data && cursors.data.length > 0 && (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.detail.resource')}</TH>
                <TH>{t('integrations.detail.cursor')}</TH>
                <TH>{t('integrations.detail.lastSyncedAt')}</TH>
                <TH>{t('integrations.detail.recordsSynced')}</TH>
              </TR>
            </THead>
            <tbody>
              {cursors.data.map((c) => (
                <TR key={c.id}>
                  <TD className="font-medium">{c.resource}</TD>
                  <TD className="max-w-[10rem] truncate text-sm text-ink-muted" title={c.cursor ?? ''}>
                    {c.cursor ??
                      (c.lastSyncedAt
                        ? t('integrations.detail.cursorExhausted')
                        : t('integrations.detail.cursorNeverStarted'))}
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">
                    {formatDateTime(c.lastSyncedAt, i18n.language)}
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">{c.recordsSynced}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {canSync && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('integrations.detail.jobKind')} controlId={`sync-kind-${installationId}`}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} className="w-40">
              {JOB_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`integrations.jobKind.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('integrations.detail.resource')} controlId={`sync-resource-${installationId}`}>
            <Input value={resource} onChange={(e) => setResource(e.target.value)} className="w-40" />
          </Field>
          <Button size="sm" disabled={busy || !isActive} onClick={() => void start()}>
            {t('integrations.detail.startJob')}
          </Button>
        </div>
      )}
      {!isActive && canSync && (
        <p className="text-xs text-ink-muted">{t('integrations.detail.syncNeedsActive')}</p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {jobs.data && jobs.data.items.length > 0 && (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.detail.jobKind')}</TH>
                <TH>{t('integrations.detail.resource')}</TH>
                <TH>{t('integrations.deliveries.status')}</TH>
                <TH>{t('integrations.detail.recordsSynced')}</TH>
                <TH className="text-end">{t('common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {jobs.data.items.map((job) => (
                <TR key={job.id}>
                  <TD className="text-sm">{t(`integrations.jobKind.${job.kind}`)}</TD>
                  <TD className="text-sm text-ink-muted">{job.resource ?? '—'}</TD>
                  <TD>
                    <Badge tone={toneForIntegrationJob(job.status)}>
                      {t(`integrations.jobStatus.${job.status}`)}
                    </Badge>
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">{job.recordsApplied}</TD>
                  <TD className="text-end">
                    {canSync && ['QUEUED', 'RUNNING'].includes(job.status) && (
                      <Button size="sm" variant="outline" onClick={() => void cancelJob(job.id)}>
                        {t('common.cancel')}
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </section>
  );
}

// ------------------------------------------------------------- deliveries

function DeliveriesSection({ installationId }: { installationId: string }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { confirm } = useConfirm();
  const { can } = usePermission();
  const canReplay = can(INTEGRATIONS_PERMISSIONS.replay);

  const [deadLettersOnly, setDeadLettersOnly] = useState(false);
  const { data, isFetching, isError, error, refetch } = useIntegrationDeliveriesQuery({
    installationId,
    limit: 25,
    deadLetteredOnly: deadLettersOnly,
  });

  const reload = () => dispatch(invalidateIntegrationDeliveries(installationId));

  const replay = async (deliveryId: string) => {
    try {
      await api.post(INTEGRATIONS_ROUTES.deliveryReplay(deliveryId));
      toast.success(t('integrations.deliveries.replayed'));
      reload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  const replayAllDeadLetters = async () => {
    const ok = await confirm({
      title: t('integrations.deliveries.replayAllTitle'),
      body: t('integrations.deliveries.replayAllBody'),
      confirmLabel: t('integrations.deliveries.replayAll'),
    });
    if (!ok) return;
    try {
      const { data } = await api.post<{ replayed: number; remaining: number }>(
        INTEGRATIONS_ROUTES.installationReplayDeadLetters(installationId),
        { limit: 100 },
      );
      toast.success(t('integrations.deliveries.replayedCount', { count: data.replayed }));
      reload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  return (
    <section className="space-y-3 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('integrations.detail.sectionDeliveries')}</h3>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={deadLettersOnly}
              onChange={(e) => setDeadLettersOnly(e.target.checked)}
            />
            {t('integrations.deliveries.deadLettersOnly')}
          </label>
          {canReplay && deadLettersOnly && (data?.items.length ?? 0) > 0 && (
            <Button size="sm" variant="outline" onClick={() => void replayAllDeadLetters()}>
              <Icon as={RefreshCw} />
              {t('integrations.deliveries.replayAll')}
            </Button>
          )}
        </div>
      </div>

      {isFetching && !data && <LoadingState />}
      {isError && !data && (
        <ErrorState message={apiMessage(error, t('integrations.states.loadFailed'))} onRetry={refetch} />
      )}
      {data && data.items.length === 0 && (
        <p className="py-6 text-center text-sm text-ink-muted">
          {deadLettersOnly
            ? t('integrations.deliveries.noDeadLetters')
            : t('integrations.deliveries.noDeliveries')}
        </p>
      )}
      {data && data.items.length > 0 && (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.deliveries.event')}</TH>
                <TH>{t('integrations.deliveries.direction')}</TH>
                <TH>{t('integrations.deliveries.status')}</TH>
                <TH>{t('integrations.deliveries.attempts')}</TH>
                <TH>{t('integrations.deliveries.idempotencyKey')}</TH>
                <TH className="text-end">{t('common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((d) => (
                <TR key={d.id}>
                  <TD className="text-sm">{d.eventName}</TD>
                  <TD className="text-sm text-ink-muted">{t(`integrations.direction.${d.direction}`)}</TD>
                  <TD>
                    <Badge tone={toneForIntegrationDelivery(d.status)}>
                      {t(`integrations.deliveryStatus.${d.status}`)}
                    </Badge>
                  </TD>
                  <TD className="text-sm tabular-nums">{d.attempts}</TD>
                  <TD className="max-w-[12rem] truncate font-mono text-xs text-ink-muted" title={d.idempotencyKey}>
                    {d.idempotencyKey}
                  </TD>
                  <TD className="text-end">
                    {canReplay && INTEGRATION_REPLAYABLE_STATUSES.includes(d.status) && (
                      <Button size="sm" variant="outline" onClick={() => void replay(d.id)}>
                        {t('integrations.deliveries.replay')}
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </section>
  );
}
